'use client';

/**
 * ErrorBoundary - Generic reusable inline error boundary component
 *
 * Wraps children with react-error-boundary to catch render errors and
 * display a styled MUI fallback with a retry button. Prevents a crash
 * in one widget from crashing the entire page.
 *
 * @see ERR-02 (Inline widget-level crash isolation)
 */
import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useTranslations } from 'next-intl';
import { Alert, Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
function DefaultFallback(t0) {
  const $ = _c(14);
  const {
    error,
    resetErrorBoundary
  } = t0;
  const tTooltip = useTranslations("tooltip");
  const message = error instanceof Error ? error.message : String(error);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      p: 2,
      border: "1px solid",
      borderColor: "error.light",
      borderRadius: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RefreshIcon />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== resetErrorBoundary) {
    t3 = <Button color="inherit" size="small" startIcon={t2} onClick={resetErrorBoundary}>Retry</Button>;
    $[2] = resetErrorBoundary;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== tTooltip) {
    t4 = tTooltip("alertComponentFailedToRender");
    $[4] = tTooltip;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <Typography variant="subtitle2">{t4}</Typography>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== message) {
    t6 = <Typography variant="body2">{message}</Typography>;
    $[8] = message;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t5 || $[12] !== t6) {
    t7 = <Box sx={t1}><Alert severity="error" action={t3}>{t5}{t6}</Alert></Box>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
  onError?: (error: unknown, info: React.ErrorInfo) => void;
}
export function ErrorBoundary(t0) {
  const $ = _c(6);
  const {
    children,
    fallback,
    onError
  } = t0;
  let t1;
  if ($[0] !== onError) {
    t1 = onError ? {
      onError
    } : {};
    $[0] = onError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const errorProps = t1;
  const t2 = fallback ?? DefaultFallback;
  let t3;
  if ($[2] !== children || $[3] !== errorProps || $[4] !== t2) {
    t3 = <ReactErrorBoundary FallbackComponent={t2} {...errorProps}>{children}</ReactErrorBoundary>;
    $[2] = children;
    $[3] = errorProps;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}